import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from '../../../../node_module_clones/recompose';
import { throttle } from 'lodash';
import { flow } from 'lodash/fp';

import liveCollaborationStoreConnect from '../../../remoteActivity/liveCollaboration/store/liveCollaborationStoreConnect';

// Utils
import { translateAndRoundFreehandPoint } from '../drawingEditor/drawingEditorElementContentUtils';

// Selectors
import { getHasLiveCollaborators } from '../../../remoteActivity/liveCollaboration/liveCollaborationSelector';
import { getCurrentUserId } from '../../../user/currentUserSelector';
import { getCurrentBoardId } from '../../selectors/currentBoardSelector';

// Actions
import { setLiveDrawingPaths, updateLiveDrawingPath } from '../store/liveDrawingActions';

const convertPointToGridUnits =
    (gridSize) =>
    ([x, y, pressure]) =>
        [x / gridSize, y / gridSize, pressure];

const mapStateToProps = createStructuredSelector({
    hasLiveCollaborators: getHasLiveCollaborators,
    currentBoardId: getCurrentBoardId,
    userId: getCurrentUserId,
});

const mapLiveCollaborationDispatchToProps = (dispatch) => ({
    dispatchSetLiveDrawingPaths: ({ paths, editedDrawingId, userId }) =>
        dispatch(
            setLiveDrawingPaths({
                paths,
                userId,
                editedDrawingId,
            }),
        ),
    dispatchUpdateLiveDrawingPath: ({ path, userId }) =>
        dispatch(
            updateLiveDrawingPath({
                path,
                userId,
            }),
        ),
});

const enhance = compose(
    connect(mapStateToProps),
    liveCollaborationStoreConnect(null, mapLiveCollaborationDispatchToProps),
);

const LiveDrawingCollaborationManager = ({
    hasLiveCollaborators,
    translation,
    paths,
    currentPath,
    userId,
    editedDrawingId,
    gridSize,
    dispatchSetLiveDrawingPaths,
    dispatchUpdateLiveDrawingPath,
}) => {
    const preparePoint = flow(translateAndRoundFreehandPoint(translation), convertPointToGridUnits(gridSize));

    useEffect(() => {
        if (!hasLiveCollaborators) return;

        const preparedPaths = paths.map(({ stroke, points, ...rest }) => ({
            ...rest,
            points: points.map(preparePoint),
        }));

        dispatchSetLiveDrawingPaths({
            paths: preparedPaths,
            userId,
            editedDrawingId,
        });
    }, [paths]);

    const throttledUpdateLiveDrawingPath = useCallback(
        throttle((path, currentUserId) => {
            // eslint-disable-next-line no-unused-vars
            const { stroke, points, ...rest } = path;

            dispatchUpdateLiveDrawingPath({
                path: {
                    ...rest,
                    points: points.map(preparePoint),
                },
                userId: currentUserId,
            });
        }, 120),
        [],
    );

    useEffect(() => {
        if (!currentPath || !hasLiveCollaborators) return;

        throttledUpdateLiveDrawingPath(currentPath, userId);
    }, [currentPath]);

    // on window or component unload, clear all live lines
    const clearLines = () =>
        dispatchSetLiveDrawingPaths({
            paths: [],
            editedDrawingId: null,
            userId,
        });

    useEffect(() => {
        window.addEventListener('beforeunload', clearLines);

        return () => {
            clearLines();

            window.removeEventListener('beforeunload', clearLines);
        };
    }, []);

    return null;
};

LiveDrawingCollaborationManager.propTypes = {
    userId: PropTypes.string,
    editedDrawingId: PropTypes.string,
    gridSize: PropTypes.number,
    hasLiveCollaborators: PropTypes.bool,
    paths: PropTypes.array,
    translation: PropTypes.object,
    currentPath: PropTypes.object,
    dispatchSetLiveDrawingPaths: PropTypes.func,
    dispatchUpdateLiveDrawingPath: PropTypes.func,
};

export default enhance(LiveDrawingCollaborationManager);
